import { Form, Row, Col, Button, Alert } from 'react-bootstrap';
import { Plus, Trash } from 'react-bootstrap-icons';
import { v4 as uuidv4 } from 'uuid';
import { ResponseMapping as ResponseMappingType, ResponseMappingEntry } from '../../../../../../../interfaces/nodeDefinitions';

const RESPONSE_TYPE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'string', label: 'String' },
  { value: 'file', label: 'File' },
  { value: 'json', label: 'JSON' },
  { value: 'xml', label: 'XML' },
];

const MAPPING_TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
];

const INFO_TEXT: Record<string, string> = {
  string: 'The entire response body will be saved as a single string value into the specified parameter.',
  file: 'The response body will be saved as a file. The Node File Name supports parameter resolution (e.g., report_${parName}.pdf).',
  json: 'Use JSONPath expressions to map values from the JSON response body to output parameters.',
  xml: 'Use XPath queries to map values from the XML response body to output parameters.',
};

const ResponseMapping = ({ state, setState }) => {
  const responseMapping: ResponseMappingType = state?.config?.response_mapping || { response_type: 'none' };
  const { response_type = 'none' } = responseMapping;
  const mappings = responseMapping.mappings || [];

  const setResponseMapping = (next: ResponseMappingType) => {
    setState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        response_mapping: next,
      },
    }));
  };

  const updateResponseMapping = (updates: Partial<ResponseMappingType>) => {
    setState((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        response_mapping: {
          ...(prev.config?.response_mapping || { response_type: 'none' }),
          ...updates,
        },
      },
    }));
  };

  const handleResponseTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value as ResponseMappingType['response_type'];
    const base: ResponseMappingType = { response_type: newType };

    if (newType === 'json' || newType === 'xml') {
      base.mappings = [];
    }

    setResponseMapping(base);
  };

  const addMappingRow = () => {
    const newEntry: ResponseMappingEntry = {
      id: uuidv4(),
      path: '',
      type: 'string',
      parameter_name: '',
      description: '',
      is_array: false,
    };
    setState((prev) => {
      const prevMappings = prev.config?.response_mapping?.mappings || [];
      return {
        ...prev,
        config: {
          ...prev.config,
          response_mapping: {
            ...(prev.config?.response_mapping || { response_type: 'none' }),
            mappings: [...prevMappings, newEntry],
          },
        },
      };
    });
  };

  const updateMappingRow = (id: string, field: keyof ResponseMappingEntry, value: any) => {
    setState((prev) => {
      const prevMappings = prev.config?.response_mapping?.mappings || [];
      return {
        ...prev,
        config: {
          ...prev.config,
          response_mapping: {
            ...(prev.config?.response_mapping || { response_type: 'none' }),
            mappings: prevMappings.map((m) => (m.id === id ? { ...m, [field]: value } : m)),
          },
        },
      };
    });
  };

  const removeMappingRow = (id: string) => {
    setState((prev) => {
      const prevMappings = prev.config?.response_mapping?.mappings || [];
      return {
        ...prev,
        config: {
          ...prev.config,
          response_mapping: {
            ...(prev.config?.response_mapping || { response_type: 'none' }),
            mappings: prevMappings.filter((m) => m.id !== id),
          },
        },
      };
    });
  };

  return (
    <>
      <Form.Group className="mb-3 w-25" controlId="form-ResponseType">
        <Form.Label>Response Type</Form.Label>
        <Form.Select name="response_type" value={response_type} onChange={handleResponseTypeChange} aria-label="Response type select">
          {RESPONSE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {response_type !== 'none' && INFO_TEXT[response_type] && (
        <Alert variant="info" className="mb-3">
          {INFO_TEXT[response_type]}
        </Alert>
      )}

      {response_type === 'string' && (
        <>
          <Form.Group className="mb-2 w-full" controlId="form-StringParamName">
            <Form.Label>
              Parameter Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={responseMapping.parameter_name || ''}
              onChange={(e) => updateResponseMapping({ parameter_name: e.target.value })}
              required
            />
          </Form.Group>
          <Form.Group className="mb-2 w-full" controlId="form-StringDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control type="text" value={responseMapping.description || ''} onChange={(e) => updateResponseMapping({ description: e.target.value })} />
          </Form.Group>
        </>
      )}

      {response_type === 'file' && (
        <>
          <Form.Group className="mb-2 w-full" controlId="form-NodeFileName">
            <Form.Label>
              Node File Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={responseMapping.node_file_name || ''}
              onChange={(e) => updateResponseMapping({ node_file_name: e.target.value })}
              placeholder="e.g., report_${parName}.pdf"
              required
            />
          </Form.Group>
          <Form.Group className="mb-2 w-full" controlId="form-FileParamName">
            <Form.Label>
              Parameter Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={responseMapping.file_parameter_name || ''}
              onChange={(e) =>
                updateResponseMapping({
                  file_parameter_name: e.target.value,
                })
              }
              required
            />
          </Form.Group>
          <Form.Group className="mb-2 w-full" controlId="form-FileDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              value={responseMapping.file_description || ''}
              onChange={(e) => updateResponseMapping({ file_description: e.target.value })}
            />
          </Form.Group>
        </>
      )}

      {(response_type === 'json' || response_type === 'xml') && (
        <>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h5 className="mb-0">{response_type === 'json' ? 'JSON' : 'XML'} Mappings</h5>
            <Button size="sm" variant="outline-primary" onClick={addMappingRow}>
              <Plus className="me-1" /> Add Mapping
            </Button>
          </div>

          {mappings.length === 0 && (
            <div className="text-muted mb-3">
              No mappings defined yet. Click <em>Add Mapping</em> to create one.
            </div>
          )}

          {mappings.map((mapping) => (
            <div key={mapping.id} className="border rounded p-3 mb-3">
              <Row className="g-3 align-items-end">
                <Col xs={12} md={3}>
                  <Form.Group controlId={`mapping-path-${mapping.id}`}>
                    <Form.Label>
                      {response_type === 'json' ? 'JSONPath' : 'XPath'} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={mapping.path}
                      onChange={(e) => updateMappingRow(mapping.id, 'path', e.target.value)}
                      placeholder={response_type === 'json' ? '$.data.value' : '/root/element'}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={2}>
                  <Form.Group controlId={`mapping-type-${mapping.id}`}>
                    <Form.Label>
                      Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select value={mapping.type} onChange={(e) => updateMappingRow(mapping.id, 'type', e.target.value)}>
                      {MAPPING_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12} md={2}>
                  <Form.Group controlId={`mapping-param-${mapping.id}`}>
                    <Form.Label>
                      Parameter Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={mapping.parameter_name}
                      onChange={(e) => updateMappingRow(mapping.id, 'parameter_name', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col xs={12} md={3}>
                  <Form.Group controlId={`mapping-desc-${mapping.id}`}>
                    <Form.Label>Description</Form.Label>
                    <Form.Control type="text" value={mapping.description} onChange={(e) => updateMappingRow(mapping.id, 'description', e.target.value)} />
                  </Form.Group>
                </Col>

                <Col xs={6} md={1}>
                  <Form.Group controlId={`mapping-array-${mapping.id}`}>
                    <Form.Check
                      type="checkbox"
                      label="Array"
                      checked={mapping.is_array}
                      onChange={(e) => updateMappingRow(mapping.id, 'is_array', e.target.checked)}
                    />
                  </Form.Group>
                </Col>

                <Col xs={6} md={1} className="d-flex">
                  <Button variant="outline-danger" className="ms-md-auto mt-2 mt-md-0" onClick={() => removeMappingRow(mapping.id)} title="Remove mapping">
                    <Trash />
                  </Button>
                </Col>
              </Row>
            </div>
          ))}
        </>
      )}
    </>
  );
};

export default ResponseMapping;
